import { useCallback } from 'react';

import type { NavigateOptions } from '@xing-com/crate-core-router';
import { useLocation, useNavigate } from '@xing-com/crate-core-router';

export type GetSearchParameter = (name: string) => string | null;
export type SetSearchParameter = (
  name: string,
  value: string,
  options?: NavigateOptions<any>
) => void;
type SetSearchParametersOptions = {
  removeEmpty?: boolean;
};
export type SetSearchParameters = (
  entries: Record<string, string>,
  options?: NavigateOptions<any> & SetSearchParametersOptions
) => void;
export type DeleteSearchParameter = (
  name: string,
  options?: NavigateOptions<any>
) => void;

export type UseSearchParameter = {
  getSearchParameter: GetSearchParameter;
  setSearchParameter: SetSearchParameter;
  setSearchParameters: SetSearchParameters;
  deleteSearchParameter: DeleteSearchParameter;
};

export type UseSearchParameterFn = () => {
  getSearchParameter: GetSearchParameter;
  setSearchParameter: SetSearchParameter;
  setSearchParameters: SetSearchParameters;
  deleteSearchParameter: DeleteSearchParameter;
};

/**
 *
 * @returns UseSearchParameter
 */
export const useSearchParameter: UseSearchParameterFn = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const getSearchParameter: GetSearchParameter = useCallback(
    (name) => {
      const search = new URLSearchParams(location.search);
      return search.get(name);
    },
    [location.search]
  );

  const setSearchParameters: SetSearchParameters = useCallback(
    (entries, { removeEmpty, ...options } = {}) => {
      const searchParams = new URLSearchParams(location.search);
      const navigateOptions =
        Object.keys(options).length > 0
          ? options
          : { replace: true, state: location.state };

      Object.entries(entries).forEach(([name, value]) => {
        if (removeEmpty && !value) {
          searchParams.delete(name);
          return;
        }

        searchParams.set(name, value);
      });

      const searchParamsString = searchParams.toString().replaceAll('+', '%20');

      navigate(
        `${location.pathname}${searchParamsString ? `?${searchParamsString}` : ''}`,
        navigateOptions
      );
    },
    [location.pathname, location.search, location.state, navigate]
  );

  const setSearchParameter: SetSearchParameter = useCallback(
    (name, value, options) => {
      setSearchParameters({ [name]: value }, options);
    },
    [setSearchParameters]
  );

  const deleteSearchParameter: DeleteSearchParameter = useCallback(
    (name, options = { replace: true, state: location.state }) => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete(name);
      const searchParamsString =
        searchParams.toString().length > 0
          ? `?${searchParams.toString().replaceAll('+', '%20')}`
          : '';

      navigate(`${location.pathname}${searchParamsString}`, options);
    },
    [location.pathname, location.search, location.state, navigate]
  );

  return {
    setSearchParameter,
    setSearchParameters,
    deleteSearchParameter,
    getSearchParameter,
  };
};
